import { apiClient } from '../api';
import type { Restaurant } from '@/types';

/**
 * Restaurant endpoints.
 *
 * This module used to export a `RestaurantService` class with twelve methods —
 * getRestaurants, getRestaurantById, updateRestaurantSettings,
 * updateBusinessHours, getRestaurantStats, updateSubscription and the rest —
 * against `/restaurants/*` paths that the backend has never mounted, plus a
 * type zoo describing fields (businessHours, stats.totalRevenue, a
 * 'trial' | 'basic' | 'pro' | 'enterprise' plan enum) that exist in no schema.
 * Nothing imported any of it. It has been removed rather than left as a
 * plausible-looking API for the next person to build a screen on.
 *
 * What the backend actually serves for a restaurant's own staff is below.
 */

/** The restaurant the signed-in user belongs to. */
export async function getMyRestaurant(): Promise<Restaurant> {
  const response = await apiClient.get<{ restaurant: Restaurant }>('/restaurants/me');
  return response.data.restaurant;
}

/**
 * The subset an owner or manager may change about their own restaurant.
 *
 * Subscription and isActive are absent on purpose: the plan is what the
 * customer pays for and isActive is how the platform suspends an account, so
 * both stay with the super-admin endpoints. The server ignores them regardless
 * of what is sent.
 */
export interface RestaurantUpdate {
  name?: string;
  description?: string;
  address?: Partial<Restaurant['address']>;
  contact?: Partial<Restaurant['contact']>;
  settings?: Partial<Omit<Restaurant['settings'], 'discountPolicy' | 'operatingHours'>> & {
    discountPolicy?: Partial<Restaurant['settings']['discountPolicy']>;
    operatingHours?: Restaurant['settings']['operatingHours'];
  };
}

/**
 * Update the caller's own restaurant. Nested objects merge server-side, so a
 * partial body only changes what it names.
 */
export async function updateMyRestaurant(updates: RestaurantUpdate): Promise<Restaurant> {
  const response = await apiClient.put<{ restaurant: Restaurant }>('/restaurants/me', updates);
  return response.data.restaurant;
}
